import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { applySeo, type SeoData } from "./head";
import {
  injectJsonLd,
  removeJsonLd,
  buildWebSiteSchema,
  buildArticleSchema,
} from "./schema";
import {
  SITE_ORIGIN,
  SITE_NAME,
  SITE_DESCRIPTION,
  HOME_TITLE,
  HOME_KEYWORDS,
  ARTICLE_KEYWORDS,
} from "./site";
import { heroImage } from "@/mocks/categories";
import { pillarArticles } from "@/mocks/articles";

type LangCode = "en" | "fr" | "da" | "de";

function buildSeoForPath(
  pathname: string,
  lang: LangCode
): { seo: SeoData; jsonLd: object | null } {
  if (pathname === "/") {
    return {
      seo: {
        title: HOME_TITLE,
        description: SITE_DESCRIPTION,
        keywords: HOME_KEYWORDS,
        canonical: `${SITE_ORIGIN}/`,
        ogType: "website",
        ogImage: heroImage,
      },
      jsonLd: buildWebSiteSchema(),
    };
  }

  const articleMatch = pathname.match(/^\/article\/([^/]+)/);
  if (articleMatch) {
    const slug = articleMatch[1];
    const article = pillarArticles.find((a) => a.slug === slug);
    if (article) {
      return {
        seo: {
          title: `${article.title[lang]} — ${SITE_NAME}`,
          description: article.meta[lang],
          keywords: ARTICLE_KEYWORDS,
          canonical: `${SITE_ORIGIN}/article/${slug}`,
          ogType: "article",
          ogImage: article.image,
        },
        jsonLd: buildArticleSchema(article, lang, slug),
      };
    }
  }

  return {
    seo: {
      title: `Page Not Found — ${SITE_NAME}`,
      description: SITE_DESCRIPTION,
      canonical: `${SITE_ORIGIN}${pathname}`,
      robots: "noindex",
    },
    jsonLd: null,
  };
}

export default function SeoManager() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const lang =
    ((i18n.language || "en").split("-")[0] as LangCode) || "en";

  useEffect(() => {
    const { seo, jsonLd } = buildSeoForPath(location.pathname, lang);
    applySeo(seo);
    if (jsonLd) {
      injectJsonLd(jsonLd);
    } else {
      removeJsonLd();
    }
  }, [location.pathname, lang]);

  return null;
}